import { type Constructor } from '../types';
import { fabric } from 'fabric';
import tools from '../tools';
import { isComment } from './guards';
import { hasEmbeddedTextBehaviors } from '../objects/guards';
import * as Sentry from '@sentry/browser';

interface CommentableBehaviors {
  prepareForEvent(eventData?: fabric.IEvent<MouseEvent>): void;
  prepareContainedComments(altKey: boolean): void;
  startMoveWithComments(): void;
  setCommentHovered(): void;
  clearCommentHovered(): void;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function CommentableBehaviors(Base: Constructor<fabric.Object>): any {
  return class extends Base implements CommentableBehaviors {
    __hasCommentableBehaviors = true;
    commentHovered = false;
    _containedComments: fabric.Object[];
    commentableLastLeft: number;
    commentableLastTop: number;
    commentableLastAngle: number;
    commentPositions: Record<string, { left: number; top: number }> = {};

    constructor() {
      super(...arguments);

      this._containedComments = [];

      this.on('mousedown', eventData => this.prepareForEvent(eventData));
      this.on('moving', () => {
        this._moveWithComments();
      });
      this.on('moved', eventData => {
        this._movedWithComments(eventData);
      });
      this.on('scaling', eventData => {
        this._scaleWithComments(eventData);
      });
      this.on('rotating', () => {
        this._rotateWithComments();
      });
      this.on('scaled', () => {
        this.commentPositions = {};
      });
    }

    deleted() {
      // For collaborators, this object will already been removed from the canvas.
      // This is fine, as the user who originally deleted the commentable will
      // still clear the comments' commentable associations.
      if (this.canvas) {
        // If the commentable is deleted, also remove the association
        // to any contained comments
        const comments = this.getContainedComments();
        comments.forEach(comment => {
          comment.set('commentable', null);
        });
        this._containedComments = [];
      }
      super.deleted(this.controller);
    }

    getContainedComments() {
      return this.canvas
        .getObjects()
        .filter(isComment)
        .filter(obj => obj.commentable === this.id);
    }

    prepareForEvent(eventData?: fabric.IEvent<MouseEvent>) {
      this.prepareContainedComments(eventData?.e.altKey ?? false);
      this._startRotateWithComments();
      this.startMoveWithComments();
      this._startScaleWithComments();
    }

    prepareContainedComments(altKey: boolean) {
      // Don't drag comments when duplicating a commentable
      if (altKey) {
        this._containedComments = [];
      } else {
        this._containedComments = this.getContainedComments();
      }
    }

    updateCommentPositions() {
      const centerPoint = this.getCenterPoint();
      // Effectively the left/top of the object before any rotation
      const unrotatedOrigin = fabric.util.rotatePoint(
        new fabric.Point(this.left, this.top),
        centerPoint,
        fabric.util.degreesToRadians((this.angle ?? 0) * -1)
      );
      const scaledWidth = this.width * this.scaleX;
      const scaledHeight = this.height * this.scaleY;

      // Before scaling the object, save position of comments relative to the
      // commentable as a decimal without any rotation applied.
      this.commentPositions = {};
      for (const comment of this._containedComments) {
        const rotated = fabric.util.rotatePoint(
          new fabric.Point(
            comment.left + comment.width / 2,
            comment.top + comment.height / 2
          ),
          centerPoint,
          fabric.util.degreesToRadians((this.angle ?? 0) * -1)
        );

        this.commentPositions[comment.id] = {
          left: (rotated.x - unrotatedOrigin.x) / scaledWidth,
          top: (rotated.y - unrotatedOrigin.y) / scaledHeight,
        };
      }
    }

    _startRotateWithComments() {
      this.commentableLastAngle = this.angle ?? 0;
    }

    _rotateWithComments() {
      if (this._containedComments.length === 0) {
        return;
      }

      const angle = this.angle ?? 0;
      const delta = angle - this.commentableLastAngle;
      const activeObj = this.canvas.getActiveObject();
      this.commentableLastAngle = angle;

      const centerPoint =
        activeObj !== this ? activeObj.getCenterPoint() : this.getCenterPoint();
      const radians = fabric.util.degreesToRadians(delta);

      for (const comment of this._containedComments) {
        const rotated = fabric.util.rotatePoint(
          new fabric.Point(
            comment.left + comment.width / 2,
            comment.top + comment.height / 2
          ),
          centerPoint,
          radians
        );

        comment.set({
          left: rotated.x - comment.width / 2,
          top: rotated.y - comment.height / 2,
        });

        comment.setCoords();
        comment.fire('moving', {
          transform: comment._currentTransform,
          commentableMoving: this.id,
        });
        if (!comment._currentTransform) {
          Sentry.captureException(
            new Error('Missing transform for comment on rotate')
          );
          return;
        }
        comment._currentTransform.last =
          fabric.util.saveObjectTransform(comment);
      }
    }

    _startScaleWithComments() {
      this.updateCommentPositions();
    }

    _scaleWithComments(eventData) {
      // If there are comments attached to this object, keep their relative
      // positions when scaling
      if (
        this._containedComments.length === 0 ||
        !Object.keys(this.commentPositions).length
      ) {
        return;
      }

      const scaledWidth = this.width * eventData.transform.last.scaleX;
      const scaledHeight = this.height * eventData.transform.last.scaleY;
      const centerPoint = this.getCenterPoint();
      // Effectively the left/top of the object before any rotation
      const unrotatedOrigin = fabric.util.rotatePoint(
        new fabric.Point(this.left, this.top),
        centerPoint,
        fabric.util.degreesToRadians((this.angle ?? 0) * -1)
      );

      for (const comment of this._containedComments) {
        if (!this.commentPositions[comment.id]) continue;

        // This is the point that the comment should be placed at, without the
        // commentable object's rotation applied.
        const point = new fabric.Point(
          unrotatedOrigin.x +
            scaledWidth * this.commentPositions[comment.id].left,
          unrotatedOrigin.y +
            scaledHeight * this.commentPositions[comment.id].top
        );

        // Apply the commentable object's rotation.
        const rotated = fabric.util.rotatePoint(
          point,
          centerPoint,
          fabric.util.degreesToRadians(this.angle ?? 0)
        );

        comment.set({
          left: rotated.x - comment.width / 2,
          top: rotated.y - comment.height / 2,
        });
        comment.setCoords();
        comment.fire('moving', {
          transform: comment._currentTransform,
          commentableMoving: this.id,
        });

        if (!comment._currentTransform) {
          Sentry.captureException(
            new Error('Missing transform for comment on scale')
          );
          return;
        }
        comment._currentTransform.last =
          fabric.util.saveObjectTransform(comment);
      }
    }

    startMoveWithComments() {
      const absPosition = this.absolutePosition();
      this.commentableLastLeft = absPosition.left;
      this.commentableLastTop = absPosition.top;

      this._containedComments.forEach(obj => {
        obj._currentTransform = {
          original: fabric.util.saveObjectTransform(obj),
          last: fabric.util.saveObjectTransform(obj),
        };
      });
    }

    _moveWithComments() {
      const absPosition = this.absolutePosition();
      const deltaX = absPosition.left - this.commentableLastLeft;
      const deltaY = absPosition.top - this.commentableLastTop;

      // bail if there is no delta
      if (deltaX || deltaY) {
        this.commentableLastLeft = absPosition.left;
        this.commentableLastTop = absPosition.top;

        this._containedComments.forEach(obj => {
          obj.set({ left: obj.left + deltaX, top: obj.top + deltaY });
          obj.setCoords();
          obj.fire('moving', {
            transform: obj._currentTransform,
            commentableMoving: this.id,
          });

          if (!obj._currentTransform) {
            Sentry.captureException(
              new Error('Missing transform for comment on move')
            );
            return;
          }
          obj._currentTransform.last = fabric.util.saveObjectTransform(obj);
        });
      }
    }

    _movedWithComments(eventData) {
      this._containedComments.forEach(obj => {
        eventData.commentableMoving = this.id;
        obj.fire('moved', { ...eventData, transform: obj._currentTransform });
      });
    }

    tools(topLevel = true) {
      const superTools = (super.tools && super.tools(topLevel)) || [];

      if (this.controller.canvas.getActiveObjects().length > 1) {
        return superTools;
      }

      if (hasEmbeddedTextBehaviors(this) && this.textEditing) {
        return superTools;
      }

      const showCommentTool = this.showCommentTool?.() ?? true;
      if (showCommentTool) {
        return [...superTools, [tools.ToolComment]];
      } else {
        return superTools;
      }
    }

    setCommentHovered() {
      this.commentHovered = true;
    }

    clearCommentHovered() {
      this.commentHovered = false;
    }

    _render(ctx: CanvasRenderingContext2D) {
      super._render(ctx);

      if (this.commentHovered) {
        const width = this.getScaledWidth();
        const height = this.getScaledHeight();

        ctx.save();
        ctx.scale(1 / this.scaleX, 1 / this.scaleY);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
        ctx.fillRect(-width / 2, -height / 2, width, height);
        ctx.restore();
      }
    }
  };
}

export default CommentableBehaviors;
